import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import Layout from "@/components/Layout";
import { useTeam } from "@/context/TeamContext";
import { useUpload } from "@/context/UploadContext";
import { toast } from "sonner";
import { TIME_OPTIONS, snapTo15 } from "@/lib/timeOptions";
import { downloadEventClip, downloadHighlightsReel, jobStatusLabel } from "@/lib/clipDownload";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VideoOverlays } from "@/components/game-detail/VideoOverlays";
import { EventForm } from "@/components/game-detail/EventForm";
import { EventsList } from "@/components/game-detail/EventsList";
import { VideoAnalysisCard } from "@/components/game-detail/VideoAnalysisCard";
import { PlayerIdentificationSection } from "@/components/game-detail/PlayerIdentificationSection";
import { ArrowLeft, House, Airplane, MapPin, CalendarBlank, SoccerBall, Trash, FloppyDisk, Plus, ShieldCheck, X, YoutubeLogo, SpinnerGap, CheckCircle, WarningCircle, ArrowClockwise, ArrowCounterClockwise, XCircle, Minus, CaretRight, CaretDown, CaretUp, PlayCircle, DownloadSimple } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
function toLocalDate(iso) {
  return toLocalInput(iso).slice(0, 10);
}
function toLocalTime(iso) {
  return snapTo15(toLocalInput(iso).slice(11, 16));
}
function scoreDisplay(val) {
  return val === "" || val === null || val === undefined ? 0 : Number(val);
}
function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}
const STAGE_LABELS = {
  preparing: "Preparing video…",
  downloading: "Downloading video…",
  downsampling: "Converting video (downsampling)…",
  splitting: "Splitting into segments…",
  analyzing: "Analyzing footage…",
};
function stageLabel(a) {
  if (!a) return "Processing…";
  if (a.stage === "analyzing" && a.total_chunks > 1) return `Analyzing part ${a.current_chunk || 1} of ${a.total_chunks}…`;
  return STAGE_LABELS[a.stage] || "Processing…";
}
function elapsedLabel(iso, now) {
  if (!iso) return null;
  const secs = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  const m = Math.floor(secs / 60), s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
function formatMinutes(totalSeconds) {
  const m = Math.round(totalSeconds / 60);
  return m < 1 ? "under a minute" : `${m} min`;
}
const EVENT_TYPE_LABELS = { goal: "Goal", shot: "Shot", save: "Save", yellow_card: "Yellow Card", red_card: "Red Card", highlight: "Highlight" };
// Yellow/Red Card and Highlight events can be attributed to "the coach"
// instead of a roster player — Goal/Assist/Shot/Save stay player-only.
const COACH_RECIPIENT_EVENT_TYPES = ["yellow_card", "red_card", "highlight"];
const EVENT_TYPE_BADGE_CLS = {
  goal: "bg-[#CCFF00]/15 text-[#CCFF00]",
  shot: "bg-blue-500/15 text-blue-400",
  save: "bg-teal-500/15 text-teal-400",
  yellow_card: "bg-yellow-500/15 text-yellow-400",
  red_card: "bg-red-500/15 text-red-400",
  highlight: "bg-fuchsia-500/15 text-fuchsia-400",
};

export default function GameDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTeamId, currentRole, loading: teamsLoading } = useTeam();
  const isViewer = currentRole === "viewer";
  const isMember = currentRole === "member";
  // "member" accounts can log/edit events + download clips, but never
  // manage the game itself (score/details/goalie/video/notes/delete) —
  // that stays owner-only, same as viewer.
  const canManageGame = !isViewer && !isMember;
  const { getUpload } = useUpload();
  const upload = getUpload(id);
  const [game, setGame] = useState(null);
  const isScheduled = game?.status === "scheduled";
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [goalieId, setGoalieId] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [bunnyInput, setBunnyInput] = useState("");
  const [attachingBunny, setAttachingBunny] = useState(false);
  const [bunnyAttachError, setBunnyAttachError] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [savingTrackId, setSavingTrackId] = useState(null);
  const [thumbIndexByTrack, setThumbIndexByTrack] = useState({});
  const [categoryFilter, setCategoryFilter] = useState("our_team_needs_review");
  const [selectedTrackIds, setSelectedTrackIds] = useState(() => new Set());
  const [bulkActing, setBulkActing] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventForm, setEventForm] = useState(null); // draft object while adding/editing, else null
  const [savingEventId, setSavingEventId] = useState(null);
  const [highlightsActive, setHighlightsActive] = useState(false);
  const [highlightsIndexDisplay, setHighlightsIndexDisplay] = useState(0);
  const [highlightsStartToken, setHighlightsStartToken] = useState(0);
  const highlightsIndexRef = useRef(0);
  const highlightsIntervalRef = useRef(null);
  const [highlight, setHighlight] = useState(null); // { trackId, box:[x1,y1,x2,y2], boxes, label }
  const highlightRef = useRef(null);
  useEffect(() => { highlightRef.current = highlight; }, [highlight]);
  const [trackBoxesCache, setTrackBoxesCache] = useState({});
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [downloadingClipId, setDownloadingClipId] = useState(null);
  const [clipDownloadStage, setClipDownloadStage] = useState(null);
  const [reelTypes, setReelTypes] = useState(() => new Set(["goal"]));
  const [downloadingReel, setDownloadingReel] = useState(false);
  const [reelDownloadStage, setReelDownloadStage] = useState(null);
  const uploadedVideoRef = useRef(null);
  const ytPlayerRef = useRef(null); // the live YouTube IFrame API player, once created
  const ytContainerRef = useRef(null);
  const ytInstanceRef = useRef(null);
  const bunnyIframeRef = useRef(null);       // the <iframe> element for a Bunny-hosted video
  const bunnyPlayerRef = useRef(null);       // the player.js Player instance wrapping it
  const bunnyCurrentTimeRef = useRef(0);     // kept in sync via player.js 'timeupdate' (its getCurrentTime is async/callback-based)
  const bunnyReadyRef = useRef(false);
  const justJumpedRef = useRef(false); // suppresses the "clear highlight on manual seek" logic for a seek WE just triggered
  const followIntervalRef = useRef(null);
  const prevUploadRef = useRef(upload);

  const skipVideo = (seconds) => {
    const v = uploadedVideoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.currentTime || 0) + seconds, v.duration || Infinity));
  };

  const stopFollowing = () => {
    if (followIntervalRef.current) {
      clearInterval(followIntervalRef.current);
      followIntervalRef.current = null;
    }
  };

  const getCurrentPlaybackTime = () => {
    if (game.video_upload_id && uploadedVideoRef.current) return uploadedVideoRef.current.currentTime;
    if (game.video_url && ytPlayerRef.current?.getCurrentTime) return ytPlayerRef.current.getCurrentTime();
    if (game.bunny_video_id) return bunnyCurrentTimeRef.current;
    return null;
  };

  // Keeps the highlight box moving with the track for a few seconds once
  // the coach presses play, instead of it freezing/vanishing the instant
  // playback resumes — falls back to just hiding once we run out of stored
  // boxes or drift too far from any of them.
  const FOLLOW_SECONDS = 6;
  const startFollowing = (trackId, boxes, fromTimestamp) => {
    stopFollowing();
    if (!boxes?.length) return;
    followIntervalRef.current = setInterval(() => {
      const t = getCurrentPlaybackTime();
      if (t == null || t - fromTimestamp > FOLLOW_SECONDS || t < fromTimestamp - 1) {
        setHighlight(null);
        stopFollowing();
        return;
      }
      let nearest = boxes[0], bestDiff = Math.abs(boxes[0].timestamp - t);
      for (const b of boxes) {
        const diff = Math.abs(b.timestamp - t);
        if (diff < bestDiff) { bestDiff = diff; nearest = b; }
      }
      if (bestDiff <= 1.5) {
        setHighlight((prev) => (prev && prev.trackId === trackId ? { ...prev, box: nearest.bbox } : prev));
      } else {
        setHighlight(null);
        stopFollowing();
      }
    }, 300);
  };

  // "Jump to this moment" from a Player Identification thumbnail — scrolls
  // the video into view, pauses on the exact detected frame with that
  // player highlighted (per spec: confirm before deciding to keep
  // watching), and — if the coach presses play — keeps the highlight
  // following that track for a few seconds using its stored bounding boxes.
  const jumpToVideoMoment = async (trackId, seconds) => {
    document.getElementById("game-video-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    stopFollowing();
    stopHighlights();
    let boxes = trackBoxesCache[trackId];
    if (!boxes) {
      try {
        const { data } = await api.get(`/teams/${currentTeamId}/games/${id}/tracks/${trackId}/boxes`);
        boxes = data;
      } catch { boxes = []; }
      setTrackBoxesCache((prev) => ({ ...prev, [trackId]: boxes }));
    }
    const nearest = boxes.length
      ? boxes.reduce((best, b) => (Math.abs(b.timestamp - seconds) < Math.abs(best.timestamp - seconds) ? b : best))
      : null;
    const matchedTrack = tracks.find((t) => t.track_id === trackId);
    const label = matchedTrack?.matched_player_id ? playerLabel(matchedTrack.matched_player_id) : `Track #${trackId}`;
    setHighlight(nearest ? { trackId, box: nearest.bbox, boxes, label } : null);
    const target = nearest ? nearest.timestamp : seconds;

    if (game.video_upload_id && uploadedVideoRef.current) {
      justJumpedRef.current = true;
      uploadedVideoRef.current.currentTime = target;
      uploadedVideoRef.current.pause();
    } else if (game.video_url && ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(target, true);
      ytPlayerRef.current.pauseVideo();
    } else if (game.bunny_video_id && bunnyPlayerRef.current) {
      justJumpedRef.current = true;
      bunnyPlayerRef.current.setCurrentTime(target);
      bunnyPlayerRef.current.pause();
    }
  };

  // Loads player.js once (Bunny Stream's official iframe-control library —
  // same programmatic seek/play/pause/timeupdate contract used everywhere
  // else in this file for YouTube/uploaded video).
  useEffect(() => {
    if (window.playerjs) return;
    if (document.getElementById("bunny-playerjs")) return;
    const script = document.createElement("script");
    script.id = "bunny-playerjs";
    script.src = "https://assets.mediadelivery.net/playerjs/playerjs-latest.min.js";
    document.body.appendChild(script);
  }, []);

  // (Re)creates the Bunny player.js instance whenever the attached Bunny
  // video changes. bunnyCurrentTimeRef is kept fresh via 'timeupdate' since
  // player.js's own getCurrentTime() is callback-based (async), unlike the
  // YouTube IFrame API's synchronous getCurrentTime().
  useEffect(() => {
    const videoId = game?.bunny_video_id;
    if (!videoId) return;
    let cancelled = false;
    bunnyReadyRef.current = false;
    const build = () => {
      if (cancelled || !bunnyIframeRef.current || !window.playerjs) return;
      const player = new window.playerjs.Player(bunnyIframeRef.current);
      player.on("ready", () => { bunnyReadyRef.current = true; });
      player.on("timeupdate", (data) => { if (typeof data?.seconds === "number") bunnyCurrentTimeRef.current = data.seconds; });
      player.on("play", () => {
        const h = highlightRef.current;
        if (h) startFollowing(h.trackId, h.boxes, bunnyCurrentTimeRef.current);
      });
      player.on("pause", stopFollowing);
      bunnyPlayerRef.current = player;
    };
    if (window.playerjs) build();
    else {
      const t = setInterval(() => { if (window.playerjs) { clearInterval(t); build(); } }, 200);
      return () => clearInterval(t);
    }
    return () => {
      cancelled = true;
      bunnyPlayerRef.current = null;
      bunnyReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.bunny_video_id]);

  // Loads the YouTube IFrame API script once (needed to seek+pause exactly
  // on a moment and to detect play/pause so the highlight overlay can
  // follow a tracked player — a plain <iframe src> can't do either).
  useEffect(() => {
    if (window.YT?.Player) return;
    if (document.getElementById("yt-iframe-api")) return;
    const script = document.createElement("script");
    script.id = "yt-iframe-api";
    script.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(script);
  }, []);

  // (Re)creates the YouTube player whenever the attached video changes.
  useEffect(() => {
    const vid = game?.video_url ? youtubeId(game.video_url) : null;
    if (!vid) return;
    let cancelled = false;
    const build = () => {
      if (cancelled || !ytContainerRef.current) return;
      const player = new window.YT.Player(ytContainerRef.current, {
        videoId: vid,
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              const h = highlightRef.current;
              if (h) startFollowing(h.trackId, h.boxes, e.target.getCurrentTime());
            } else if (e.data === window.YT.PlayerState.PAUSED) {
              stopFollowing();
            }
          },
        },
      });
      ytInstanceRef.current = player;
      ytPlayerRef.current = player;
    };
    if (window.YT?.Player) build();
    else {
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prevReady?.(); build(); };
    }
    return () => {
      cancelled = true;
      if (ytInstanceRef.current) {
        try { ytInstanceRef.current.destroy(); } catch { /* already gone */ }
      }
      ytInstanceRef.current = null;
      ytPlayerRef.current = null;
    };
  }, [game?.video_url]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGame = async () => {
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/games/${id}`);
      setGame(data);
      fetchAnalysis();
    } catch { /* keep whatever we already have on screen */ }
  };

  // The upload can finish (or be cancelled) while the coach is on a
  // different page entirely — UploadContext survives navigation. When it
  // clears for this game, refetch so video_upload_id/video_url reflect reality.
  useEffect(() => {
    if (prevUploadRef.current && !upload) fetchGame();
    prevUploadRef.current = upload;
  }, [upload]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrapped in useCallback (stable unless the team/game changes) so the
  // mount effect below and the analysis-polling effect can both list them
  // as real dependencies instead of needing an exhaustive-deps escape hatch.
  const fetchAnalysis = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/games/${id}/analysis`);
      setAnalysis(data);
      return data;
    } catch {
      return null;
    }
  }, [currentTeamId, id]);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/games/${id}/events`);
      setEvents(data);
    } catch { /* keep whatever we already have on screen */ }
    finally { setEventsLoading(false); }
  }, [currentTeamId, id]);

  useEffect(() => {
    if (teamsLoading) return;
    if (!currentTeamId) {
      navigate("/games");
      return;
    }
    (async () => {
      try {
        const [gameRes, playersRes] = await Promise.all([
          api.get(`/teams/${currentTeamId}/games/${id}`),
          api.get(`/teams/${currentTeamId}/players`),
        ]);
        const data = gameRes.data;
        setGame(data);
        setPlayers(playersRes.data);
        setForm({
          opponent: data.opponent,
          date: toLocalDate(data.date),
          time: toLocalTime(data.date),
          location: data.location || "",
          is_home: data.is_home,
          completed: data.status === "completed",
          score_for: data.score_for ?? "",
          score_against: data.score_against ?? "",
          notes: data.notes || "",
        });
        setGoalieId(data.goalie_player_id || "");
        setVideoUrl(data.video_url || "");
        // A completed analysis (and its Player Identification tracks) can
        // outlive the video attachment itself (e.g. video removed after
        // review) — always fetch, don't gate on video_url/video_upload_id.
        fetchAnalysis();
        fetchEvents();
      } catch (e) {
        navigate("/games");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, currentTeamId, teamsLoading, navigate, fetchAnalysis, fetchEvents]);

  // Poll while a job is queued/processing.
  useEffect(() => {
    if (!analysis || !["queued", "processing"].includes(analysis.status)) return;
    const t = setInterval(fetchAnalysis, 3000);
    return () => clearInterval(t);
  }, [analysis?.status, fetchAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-tick the elapsed/last-update clocks while a job is running.
  useEffect(() => {
    if (!analysis || !["queued", "processing"].includes(analysis.status)) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [analysis?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTracks = async () => {
    setTracksLoading(true);
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/games/${id}/tracks`);
      setTracks(data);
    } catch { /* keep whatever we already have on screen */ }
    finally { setTracksLoading(false); }
  };

  // --- Game events (V5): goals/shots, AI-suggested or coach-logged ---------
  const formatEventTime = (seconds) => {
    const s = Math.max(0, Math.floor(seconds || 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  // Coaches type/see event moments as MM:SS (matching the video player and
  // the Events list) rather than raw seconds — parsed back to seconds here.
  const parseEventTime = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const str = String(value).trim();
    if (str.includes(":")) {
      const parts = str.split(":").map((p) => parseFloat(p) || 0);
      return parts.length >= 2 ? parts[parts.length - 2] * 60 + parts[parts.length - 1] : parts[0];
    }
    return parseFloat(str) || 0;
  };

  // Confirmed goal events drive score_for/score_against server-side — pull
  // the fresh values back into the form so the "Final score" card above
  // reflects it immediately, without the coach needing to reload.
  const refreshGameScore = async () => {
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/games/${id}`);
      setGame(data);
      setForm((f) => (f ? { ...f, score_for: data.score_for ?? "", score_against: data.score_against ?? "" } : f));
    } catch { /* non-critical — the score will still be correct on next reload */ }
  };

  // Events aren't tied to a specific detected track — jumps to the start of
  // this event's clip window (this event's own stored Seconds Prior, or
  // the 10s app-wide default if it predates this per-event override) and
  // plays from there, matching the download/reel clip window below.
  const jumpToEventMoment = (ev) => {
    stopHighlights();
    setHighlight(null);
    const preRoll = ev.pre_roll_seconds ?? EVENT_CLIP_PRE_ROLL_SECONDS;
    const target = Math.max(0, ev.timestamp - preRoll);
    if (game.video_upload_id && uploadedVideoRef.current) {
      justJumpedRef.current = true;
      uploadedVideoRef.current.currentTime = target;
      uploadedVideoRef.current.play().catch(() => {});
    } else if (game.video_url && ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(target, true);
      ytPlayerRef.current.playVideo();
    } else if (game.bunny_video_id && bunnyPlayerRef.current) {
      justJumpedRef.current = true;
      bunnyPlayerRef.current.setCurrentTime(target);
      bunnyPlayerRef.current.play();
    }
  };

  const startAddEvent = (team = "our_team") => setEventForm({
    type: "goal", team: typeof team === "string" ? team : "our_team", player_id: "", recipient_type: "player",
    timestamp: formatEventTime(Math.floor(getCurrentPlaybackTime() || 0)), outcome: "",
    assist_team: "", assist_player_id: "", linked_shot_event_id: "",
    pre_roll_seconds: 10, post_roll_seconds: 5,
  });
  const startEditEvent = (ev) => setEventForm({
    id: ev.id, type: ev.type, team: ev.team, player_id: ev.player_id || "",
    recipient_type: ev.recipient_type === "coach" ? "coach" : "player",
    timestamp: formatEventTime(ev.timestamp), outcome: ev.outcome || "",
    assist_team: ev.assist_team || "", assist_player_id: ev.assist_player_id || "",
    linked_shot_event_id: ev.linked_shot_event_id || "",
    pre_roll_seconds: ev.pre_roll_seconds ?? 10, post_roll_seconds: ev.post_roll_seconds ?? 5,
  });
  const cancelEventForm = () => setEventForm(null);

  const saveEventForm = async () => {
    if (!eventForm) return;
    const isCoach = COACH_RECIPIENT_EVENT_TYPES.includes(eventForm.type) && eventForm.recipient_type === "coach";
    const payload = {
      type: eventForm.type, team: (eventForm.type === "save" || eventForm.type === "highlight") ? "our_team" : eventForm.team,
      player_id: isCoach ? null : ((eventForm.type === "save" || eventForm.type === "highlight" || eventForm.team === "our_team") ? (eventForm.player_id || null) : null),
      recipient_type: isCoach ? "coach" : "player",
      timestamp: parseEventTime(eventForm.timestamp),
      outcome: eventForm.type === "shot" ? (eventForm.outcome || null) : null,
      assist_team: eventForm.type === "goal" ? (eventForm.assist_team || null) : null,
      assist_player_id: eventForm.type === "goal" && eventForm.assist_team === "our_team" ? (eventForm.assist_player_id || null) : null,
      linked_shot_event_id: eventForm.type === "save" ? (eventForm.linked_shot_event_id || null) : null,
      pre_roll_seconds: Math.max(0, Math.min(Number(eventForm.pre_roll_seconds) || 0, 120)),
      post_roll_seconds: Math.max(0, Math.min(Number(eventForm.post_roll_seconds) || 0, 120)),
    };
    try {
      if (eventForm.id) {
        const { data } = await api.put(`/teams/${currentTeamId}/games/${id}/events/${eventForm.id}`, payload);
        setEvents((prev) => prev.map((e) => (e.id === eventForm.id ? data : e)).sort((a, b) => a.timestamp - b.timestamp));
        toast.success("Event updated");
      } else {
        const { data } = await api.post(`/teams/${currentTeamId}/games/${id}/events`, payload);
        setEvents((prev) => [...prev, data].sort((a, b) => a.timestamp - b.timestamp));
        toast.success("Event added");
      }
      setEventForm(null);
      await refreshGameScore();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const confirmEvent = async (ev) => {
    setSavingEventId(ev.id);
    try {
      const { data } = await api.put(`/teams/${currentTeamId}/games/${id}/events/${ev.id}`, { status: "confirmed" });
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? data : e)));
      toast.success("Event confirmed");
      await refreshGameScore();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingEventId(null);
    }
  };

  const deleteEvent = async (ev) => {
    setSavingEventId(ev.id);
    try {
      await api.delete(`/teams/${currentTeamId}/games/${id}/events/${ev.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      toast.success("Event deleted");
      await refreshGameScore();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingEventId(null);
    }
  };

  // Server-side clip download (Part 3) — reused for the Events list and
  // the highlights banner below; extracts+trims on the backend so the
  // coach never has to download the full game video.
  const handleDownloadClip = async (ev) => {
    setDownloadingClipId(ev.id);
    setClipDownloadStage("Preparing your download…");
    try {
      await downloadEventClip(currentTeamId, id, ev.id, (job) => setClipDownloadStage(jobStatusLabel(job)));
      toast.success("Clip downloaded");
    } catch (err) {
      toast.error(err.message || "Could not download clip");
    } finally {
      setDownloadingClipId(null);
      setClipDownloadStage(null);
    }
  };

  // --- Highlights reel: coach picks which event types to include (Goals/
  // Saves/Shots, defaulting to Goals only) and plays the matching confirmed
  // events back to back, each using the same 10s-before/5s-after clip
  // window as individual clip downloads. ---
  const EVENT_CLIP_PRE_ROLL_SECONDS = 10;
  const EVENT_CLIP_POST_ROLL_SECONDS = 5;

  const reelEventsSorted = () =>
    events.filter((e) => e.status === "confirmed" && reelTypes.has(e.type)).sort((a, b) => a.timestamp - b.timestamp);

  // Same "who" attribution logic used by both the highlights-reel event
  // label and each row in the Events list below — kept in one place so the
  // two never drift.
  const eventWhoLabel = (ev) => {
    if (ev.team !== "our_team") return "Opposing team";
    if (ev.recipient_type === "coach") return "Coach";
    if (ev.player_id) return playerLabel(ev.player_id);
    return ev.type === "save" ? "Goalkeeper unknown" : "Player unknown";
  };

  const eventLabel = (ev) => `${EVENT_TYPE_LABELS[ev.type]} by ${eventWhoLabel(ev)}`;

  const toggleReelType = (t) => setReelTypes((prev) => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });

  const playClipAt = (ev) => {
    const preRoll = ev.pre_roll_seconds ?? EVENT_CLIP_PRE_ROLL_SECONDS;
    const target = Math.max(0, ev.timestamp - preRoll);
    setHighlight(null);
    if (game.video_upload_id && uploadedVideoRef.current) {
      justJumpedRef.current = true;
      uploadedVideoRef.current.currentTime = target;
      uploadedVideoRef.current.play().catch(() => {});
    } else if (game.video_url && ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(target, true);
      ytPlayerRef.current.playVideo();
    } else if (game.bunny_video_id && bunnyPlayerRef.current) {
      justJumpedRef.current = true;
      bunnyPlayerRef.current.setCurrentTime(target);
      bunnyPlayerRef.current.play();
    }
  };

  const stopHighlights = () => {
    if (highlightsIntervalRef.current) {
      clearInterval(highlightsIntervalRef.current);
      highlightsIntervalRef.current = null;
    }
    setHighlightsActive(false);
  };

  const startHighlights = () => {
    const clips = reelEventsSorted();
    if (!clips.length) return;
    setHighlightsActive(true);
    highlightsIndexRef.current = 0;
    setHighlightsIndexDisplay(0);
    // Bumping this token (rather than calling playClipAt(clips[0]) directly
    // here) defers the actual seek+play to the effect below, which only
    // fires after React has committed the render — guaranteeing the video/
    // YouTube player refs are attached. Calling it synchronously worked
    // here already since the video elements are always mounted regardless
    // of highlightsActive, but the token pattern is kept consistent with
    // the cross-game reel on the Stats page (see startReel there), which
    // DOES need this deferral to fix a real "Play doesn't start" bug.
    setHighlightsStartToken((t) => t + 1);
    if (highlightsIntervalRef.current) clearInterval(highlightsIntervalRef.current);
    highlightsIntervalRef.current = setInterval(() => {
      const clips2 = reelEventsSorted();
      const idx = highlightsIndexRef.current;
      const ev = clips2[idx];
      if (!ev) return;
      const t = getCurrentPlaybackTime();
      const postRoll = ev.post_roll_seconds ?? EVENT_CLIP_POST_ROLL_SECONDS;
      if (t == null || t < ev.timestamp + postRoll) return;
      const nextIdx = idx + 1;
      if (nextIdx >= clips2.length) {
        stopHighlights();
        if (game.video_upload_id && uploadedVideoRef.current) uploadedVideoRef.current.pause();
        else if (game.video_url && ytPlayerRef.current?.pauseVideo) ytPlayerRef.current.pauseVideo();
        else if (game.bunny_video_id && bunnyPlayerRef.current) bunnyPlayerRef.current.pause();
        return;
      }
      highlightsIndexRef.current = nextIdx;
      setHighlightsIndexDisplay(nextIdx);
      playClipAt(clips2[nextIdx]);
    }, 300);
  };

  // Fires the actual first play once the video/YouTube player for this
  // reel is guaranteed to be mounted (see the comment in startHighlights).
  useEffect(() => {
    if (highlightsStartToken > 0) {
      const clips = reelEventsSorted();
      playClipAt(clips[highlightsIndexRef.current]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightsStartToken]);

  const handleDownloadReel = async () => {
    const types = Array.from(reelTypes);
    if (types.length === 0) return;
    setDownloadingReel(true);
    setReelDownloadStage("Preparing your download…");
    try {
      await downloadHighlightsReel(currentTeamId, id, types, (job) => setReelDownloadStage(jobStatusLabel(job)));
      toast.success("Highlight reel downloaded");
    } catch (err) {
      toast.error(err.message || "Could not build highlight reel");
    } finally {
      setDownloadingReel(false);
      setReelDownloadStage(null);
    }
  };

  // Track identities only exist once analysis finishes — fetch then, and
  // reset when switching to a different game.
  useEffect(() => {
    setTracks([]);
    setSelectedTrackIds(new Set());
    setCategoryFilter("our_team_needs_review");
    if (analysis?.status === "complete") fetchTracks();
  }, [analysis?.status, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTrackMatch = async (trackId, playerId) => {
    setSavingTrackId(trackId);
    try {
      const { data } = await api.put(`/teams/${currentTeamId}/games/${id}/tracks/${trackId}`, { matched_player_id: playerId || null });
      setTracks((prev) => prev.map((t) => (t.track_id === trackId ? data : t)));
      toast.success(playerId ? "Player assigned" : "Match cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingTrackId(null);
    }
  };

  const dismissTrack = async (trackId, category) => {
    setSavingTrackId(trackId);
    try {
      const { data } = await api.put(`/teams/${currentTeamId}/games/${id}/tracks/${trackId}`, { category });
      setTracks((prev) => prev.map((t) => (t.track_id === trackId ? data : t)));
      toast.success(category === "not_a_player" ? "Marked as not a player" : "Marked as opposing team");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingTrackId(null);
    }
  };

  const restoreTrack = async (trackId) => {
    setSavingTrackId(trackId);
    try {
      const { data } = await api.put(`/teams/${currentTeamId}/games/${id}/tracks/${trackId}`, { category: "our_team_needs_review" });
      setTracks((prev) => prev.map((t) => (t.track_id === trackId ? data : t)));
      toast.success("Moved back to review");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingTrackId(null);
    }
  };

  const bulkDismiss = async (category) => {
    const trackIds = Array.from(selectedTrackIds);
    if (!trackIds.length) return;
    setBulkActing(true);
    try {
      await api.put(`/teams/${currentTeamId}/games/${id}/tracks/bulk-category`, { track_ids: trackIds, category });
      setTracks((prev) => prev.map((t) => (
        selectedTrackIds.has(t.track_id)
          ? { ...t, category, matched_player_id: null, match_confidence: null, match_source: null }
          : t
      )));
      toast.success(`${trackIds.length} track${trackIds.length === 1 ? "" : "s"} marked as ${category === "not_a_player" ? "not a player" : "opposing team"}`);
      setSelectedTrackIds(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBulkActing(false);
    }
  };

  const cancelAnalysis = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/teams/${currentTeamId}/games/${id}/analysis/cancel`);
      setAnalysis(data);
      toast.success("Analysis cancelled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCancelling(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/teams/${currentTeamId}/games/${id}/analyze`);
      setAnalysis(data);
      toast.success("Analysis started — this runs in the background.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const playerLabel = (pid) => {
    const p = players.find((x) => x.id === pid);
    return p ? `#${p.jersey_number} ${p.first_name} ${p.last_name}` : "Unknown player";
  };

  const isCleanSheet = form?.completed && String(form?.score_against) === "0";

  const save = async (e) => {
    e.preventDefault();
    await persist({});
  };

  const persist = async (overrides) => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        opponent: form.opponent.trim(),
        date: new Date(`${form.date}T${form.time}`).toISOString(),
        location: form.location.trim(),
        is_home: form.is_home,
        score_for: form.completed ? (scoreDisplay(form.score_for)) : null,
        score_against: form.completed ? (scoreDisplay(form.score_against)) : null,
        notes: form.notes,
        goalie_player_id: goalieId || null,
        video_url: videoUrl.trim() || null,
        ...overrides,
      };
      const { data } = await api.put(`/teams/${currentTeamId}/games/${id}`, payload);
      setGame(data);
      setVideoUrl(data.video_url || "");
      toast.success("Game saved");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeVideo = async () => {
    setVideoUrl("");
    await persist({ video_url: null });
    toast.success("Video removed");
  };

  const removeUploadedVideo = async () => {
    try {
      await api.delete(`/teams/${currentTeamId}/games/${id}/video-upload`);
      setGame({ ...game, video_upload_id: null });
      toast.success("Video removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeBunnyVideo = async () => {
    try {
      await api.delete(`/teams/${currentTeamId}/games/${id}/video-bunny`);
      setGame({ ...game, bunny_video_id: null });
      toast.success("Video removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const attachBunnyVideo = async () => {
    const value = bunnyInput.trim();
    if (!value) return;
    setAttachingBunny(true);
    setBunnyAttachError("");
    try {
      const { data } = await api.post(`/teams/${currentTeamId}/games/${id}/video-bunny/attach`, { value });
      setGame(data);
      setBunnyInput("");
      toast.success("Video attached from Bunny.net.");
    } catch (err) {
      setBunnyAttachError(err.response?.data?.detail || err.message || "Could not attach this video.");
    } finally {
      setAttachingBunny(false);
    }
  };

  const del = async () => {
    await api.delete(`/teams/${currentTeamId}/games/${id}`);
    toast.success("Game deleted");
    navigate("/games");
  };

  if (loading || !form) {
    return (
      <Layout>
        <div className="flex justify-center py-32">
          <SoccerBall size={40} className="text-[#CCFF00] animate-spin" weight="fill" />
        </div>
      </Layout>
    );
  }

  const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 text-white px-3 py-2 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150 [color-scheme:dark]";

  return (
    <Layout>
      <button onClick={() => navigate("/games")} data-testid="back-to-games" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors duration-150 mb-6 font-label text-xs">
        <ArrowLeft size={16} weight="bold" /> Back to games
      </button>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 shrink-0 bg-[#121212] border border-zinc-800 rounded-sm flex items-center justify-center">
          {game.is_home ? <House size={30} className="text-[#CCFF00]" /> : <Airplane size={30} className="text-[#CCFF00]" />}
        </div>
        <div>
          <p className="font-label text-xs text-[#CCFF00] mb-1">{game.is_home ? "Home" : "Away"} game</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none" data-testid="game-detail-title">vs {game.opponent}</h1>
          <p className="text-sm text-zinc-500 mt-1 flex items-center gap-2">
            <CalendarBlank size={14} /> {formatDate(game.date)}
            {game.location && <><MapPin size={14} className="ml-1" /> {game.location}</>}
          </p>
        </div>
      </motion.div>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <fieldset disabled={!canManageGame} className="contents">
        <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl text-white">Final score</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="font-label text-[10px] text-zinc-500 uppercase">{form.completed ? "Match completed" : "Scheduled"}</span>
              <Switch
                checked={form.completed}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  completed: checked,
                  score_for: checked ? (form.score_for === "" ? "0" : form.score_for) : form.score_for,
                  score_against: checked ? (form.score_against === "" ? "0" : form.score_against) : form.score_against,
                })}
                data-testid="game-completed-toggle"
              />
            </label>
          </div>

          {!form.completed ? (
            <p className="text-sm text-zinc-500 text-center py-6" data-testid="score-not-played-message">
              Scheduled — no score yet. Flip "Match completed" once this game has been played to enter a final score.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="font-label text-[10px] text-zinc-500 mb-2">Us</p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setForm({ ...form, score_for: Math.max(0, scoreDisplay(form.score_for) - 1) })} disabled={scoreDisplay(form.score_for) === 0} data-testid="score-for-minus" className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#0A0A0A] border border-zinc-800 text-zinc-400 rounded-sm hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors duration-150 disabled:opacity-30 disabled:hover:border-zinc-800 disabled:hover:text-zinc-400">
                      <Minus size={14} weight="bold" />
                    </button>
                    <input type="number" min="0" value={scoreDisplay(form.score_for)} onChange={(e) => setForm({ ...form, score_for: e.target.value })} data-testid="score-for" className="w-16 bg-[#0A0A0A] border border-zinc-800 text-white text-center font-heading text-5xl py-3 rounded-sm focus:outline-none focus:border-[#CCFF00]" />
                    <button type="button" onClick={() => setForm({ ...form, score_for: scoreDisplay(form.score_for) + 1 })} data-testid="score-for-plus" className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#0A0A0A] border border-zinc-800 text-zinc-400 rounded-sm hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors duration-150">
                      <Plus size={14} weight="bold" />
                    </button>
                  </div>
                </div>
                <span className="font-heading text-4xl text-zinc-600">:</span>
                <div className="text-center">
                  <p className="font-label text-[10px] text-zinc-500 mb-2">{game.opponent}</p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setForm({ ...form, score_against: Math.max(0, scoreDisplay(form.score_against) - 1) })} disabled={scoreDisplay(form.score_against) === 0} data-testid="score-against-minus" className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#0A0A0A] border border-zinc-800 text-zinc-400 rounded-sm hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors duration-150 disabled:opacity-30 disabled:hover:border-zinc-800 disabled:hover:text-zinc-400">
                      <Minus size={14} weight="bold" />
                    </button>
                    <input type="number" min="0" value={scoreDisplay(form.score_against)} onChange={(e) => setForm({ ...form, score_against: e.target.value })} data-testid="score-against" className="w-16 bg-[#0A0A0A] border border-zinc-800 text-white text-center font-heading text-5xl py-3 rounded-sm focus:outline-none focus:border-[#CCFF00]" />
                    <button type="button" onClick={() => setForm({ ...form, score_against: scoreDisplay(form.score_against) + 1 })} data-testid="score-against-plus" className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#0A0A0A] border border-zinc-800 text-zinc-400 rounded-sm hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors duration-150">
                      <Plus size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-800/70 space-y-1.5" data-testid="score-reconciliation">
                {(() => {
                  const rows = [
                    { key: "us", label: "Us", team: "our_team", final: scoreDisplay(form.score_for) },
                    { key: "opponent", label: game.opponent, team: "opposing_team", final: scoreDisplay(form.score_against) },
                  ].map((r) => ({
                    ...r,
                    logged: events.filter((e) => e.type === "goal" && e.team === r.team && e.status === "confirmed").length,
                  }));
                  if (rows.every((r) => r.final === 0 && r.logged === 0)) {
                    return <p className="text-xs text-zinc-600 text-center">Tap +/- to update the score, or log goals as Events below to build Season Stats.</p>;
                  }
                  return rows.map((r) => {
                    if (r.final === 0 && r.logged === 0) return null;
                    const matches = r.final === r.logged;
                    return (
                      <p key={r.key} className={`text-xs flex items-center justify-center gap-1.5 flex-wrap ${matches ? "text-zinc-500" : "text-amber-400"}`} data-testid={`reconciliation-${r.key}`}>
                        {matches ? <CheckCircle size={13} weight="fill" className="text-[#32D74B] shrink-0" /> : <WarningCircle size={13} weight="fill" className="shrink-0" />}
                        <span>{r.label}: {r.final} goal{r.final !== 1 ? "s" : ""} in final score — {r.logged} logged as event{r.logged !== 1 ? "s" : ""}</span>
                        {!matches && !isViewer && (
                          <button
                            type="button"
                            onClick={() => { startAddEvent(r.team); document.getElementById("events-section")?.scrollIntoView({ behavior: "smooth" }); }}
                            data-testid={`reconciliation-add-event-${r.key}`}
                            className="text-[#CCFF00] hover:underline"
                          >
                            Add event
                          </button>
                        )}
                      </p>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </div>

        <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm space-y-4">
          <h2 className="font-heading text-2xl text-white mb-2">Details</h2>
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Opponent</label>
            <input required value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} data-testid="detail-opponent" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Date &amp; time</label>
              <div className="flex gap-2">
                <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="detail-date" className={inputCls} />
                <select required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="detail-time" className={inputCls}>
                  {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="detail-location" className={inputCls} />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, is_home: true })} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs ${form.is_home ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
              <House size={16} /> Home
            </button>
            <button type="button" onClick={() => setForm({ ...form, is_home: false })} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs ${!form.is_home ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
              <Airplane size={16} /> Away
            </button>
          </div>
        </div>

        {/* Goalie */}
        <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm" data-testid="goalie-card">
          <h2 className="font-heading text-2xl text-white flex items-center gap-2 mb-2"><ShieldCheck size={22} weight="fill" className="text-[#CCFF00]" /> Goalkeeper</h2>
          {players.length === 0 ? (
            <p className="text-zinc-600 text-sm">Add players to your roster to record who played goalie.</p>
          ) : (
            <>
              <label className="font-label text-xs text-zinc-400 block mb-2">Who played goalie?</label>
              <select value={goalieId} onChange={(e) => setGoalieId(e.target.value)} data-testid="goalie-select" className={inputCls}>
                <option value="" className="bg-[#121212]">No goalie recorded</option>
                {players.map((p) => (
                  <option key={p.id} value={p.id} className="bg-[#121212]">#{p.jersey_number} {p.first_name} {p.last_name}</option>
                ))}
              </select>
              {isCleanSheet && goalieId && (
                <p className="text-xs text-[#32D74B] mt-3 flex items-center gap-1" data-testid="clean-sheet-credit">
                  <ShieldCheck size={14} weight="fill" /> Clean sheet credited to {playerLabel(goalieId)}
                </p>
              )}
              {isCleanSheet && !goalieId && (
                <p className="text-xs text-zinc-500 mt-3" data-testid="clean-sheet-hint">Select a goalie above to credit this clean sheet in stats.</p>
              )}
            </>
          )}
        </div>
        </fieldset>

        {/* Game video */}
        <div id="game-video-section" className="bg-[#121212] border border-zinc-800 p-6 rounded-sm lg:col-span-2" data-testid="video-card">
          <h2 className="font-heading text-2xl text-white flex items-center gap-2 mb-4"><YoutubeLogo size={24} weight="fill" className="text-[#FF3B30]" /> Game video</h2>
          {game.video_url && youtubeId(game.video_url) ? (
            <div className="space-y-3">
              <div className="relative w-full overflow-hidden rounded-sm bg-black" style={{ paddingTop: "56.25%" }}>
                <div data-testid="video-player" className="absolute inset-0 w-full h-full">
                  <div ref={ytContainerRef} className="w-full h-full" />
                </div>
                <VideoOverlays
                  highlight={highlight} analysis={analysis} highlightsActive={highlightsActive}
                  clip={reelEventsSorted()[highlightsIndexDisplay]} clipIndexDisplay={highlightsIndexDisplay}
                  clipsCount={reelEventsSorted().length} eventLabel={eventLabel} isViewer={isViewer}
                  downloadingClipId={downloadingClipId} clipDownloadStage={clipDownloadStage}
                  onDownloadClip={handleDownloadClip} onStopHighlights={stopHighlights}
                />
              </div>
              {canManageGame && (
                <button type="button" onClick={removeVideo} disabled={saving} data-testid="remove-video-btn" className="flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs disabled:opacity-50">
                  <Trash size={16} weight="bold" /> Remove video
                </button>
              )}
            </div>
          ) : game.video_upload_id ? (
            <div className="space-y-3">
              <div className="relative w-full overflow-hidden rounded-sm bg-black" style={{ paddingTop: "56.25%" }}>
                <video
                  ref={uploadedVideoRef}
                  data-testid="uploaded-video-player"
                  className="absolute inset-0 w-full h-full"
                  src={`${API}/teams/${currentTeamId}/games/${id}/video-upload/stream`}
                  controls
                  preload="metadata"
                  onPlay={() => {
                    const h = highlightRef.current;
                    if (h) startFollowing(h.trackId, h.boxes, uploadedVideoRef.current.currentTime);
                  }}
                  onPause={stopFollowing}
                  onSeeked={() => {
                    if (!justJumpedRef.current) { setHighlight(null); stopFollowing(); }
                    justJumpedRef.current = false;
                  }}
                />
                <VideoOverlays
                  highlight={highlight} analysis={analysis} highlightsActive={highlightsActive}
                  clip={reelEventsSorted()[highlightsIndexDisplay]} clipIndexDisplay={highlightsIndexDisplay}
                  clipsCount={reelEventsSorted().length} eventLabel={eventLabel} isViewer={isViewer}
                  downloadingClipId={downloadingClipId} clipDownloadStage={clipDownloadStage}
                  onDownloadClip={handleDownloadClip} onStopHighlights={stopHighlights}
                />
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => skipVideo(-10)} data-testid="skip-back-btn" className="flex items-center gap-1.5 text-zinc-400 hover:text-[#CCFF00] transition-colors duration-150 font-label text-xs">
                  <ArrowCounterClockwise size={16} weight="bold" /> 10s
                </button>
                <button type="button" onClick={() => skipVideo(10)} data-testid="skip-forward-btn" className="flex items-center gap-1.5 text-zinc-400 hover:text-[#CCFF00] transition-colors duration-150 font-label text-xs">
                  <ArrowClockwise size={16} weight="bold" /> 10s
                </button>
              </div>
              {canManageGame && (
                <button type="button" onClick={removeUploadedVideo} data-testid="remove-uploaded-video-btn" className="flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs">
                  <Trash size={16} weight="bold" /> Remove video
                </button>
              )}
            </div>
          ) : game.bunny_video_id ? (
            <div className="space-y-3">
              <div className="relative w-full overflow-hidden rounded-sm bg-black" style={{ paddingTop: "56.25%" }}>
                <iframe
                  ref={bunnyIframeRef}
                  key={game.bunny_video_id}
                  data-testid="bunny-video-player"
                  className="absolute inset-0 w-full h-full border-0"
                  src={`https://iframe.mediadelivery.net/embed/${game.bunny_library_id}/${game.bunny_video_id}`}
                  loading="lazy"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
                <VideoOverlays
                  highlight={highlight} analysis={analysis} highlightsActive={highlightsActive}
                  clip={reelEventsSorted()[highlightsIndexDisplay]} clipIndexDisplay={highlightsIndexDisplay}
                  clipsCount={reelEventsSorted().length} eventLabel={eventLabel} isViewer={isViewer}
                  downloadingClipId={downloadingClipId} clipDownloadStage={clipDownloadStage}
                  onDownloadClip={handleDownloadClip} onStopHighlights={stopHighlights}
                />
              </div>
              <p className="text-xs text-zinc-600">Hosted on Bunny.net Stream. If it says "processing", give it a few minutes and refresh.</p>
              {canManageGame && (
                <button type="button" onClick={removeBunnyVideo} data-testid="remove-bunny-video-btn" className="flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs">
                  <Trash size={16} weight="bold" /> Remove video
                </button>
              )}
            </div>
          ) : !canManageGame ? (
            <p className="text-zinc-600 text-sm" data-testid="no-video-attached">No video attached to this game yet.</p>
          ) : (
            <div className="space-y-3">
              <label className="font-label text-xs text-zinc-400 block">Paste your Bunny.net video link or ID</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={bunnyInput}
                  onChange={(e) => setBunnyInput(e.target.value)}
                  data-testid="bunny-video-input"
                  placeholder="https://iframe.mediadelivery.net/embed/… or the Video ID"
                  className={inputCls}
                />
                <button
                  type="button"
                  onClick={attachBunnyVideo}
                  disabled={attachingBunny || !bunnyInput.trim()}
                  data-testid="attach-bunny-video-btn"
                  className="shrink-0 flex items-center justify-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-40"
                >
                  <Plus size={14} weight="bold" /> {attachingBunny ? "Checking…" : "Attach"}
                </button>
              </div>
              {bunnyAttachError && <p className="text-xs text-[#FF3B30]" data-testid="bunny-attach-error">{bunnyAttachError}</p>}
              <p className="text-xs text-zinc-600">
                Upload the video on <a href="https://dash.bunny.net" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#CCFF00] underline">Bunny.net's dashboard</a> first, then paste the Video URL (or just the Video ID) it gives you here.
              </p>
            </div>
          )}
        </div>

        {/* Events (V5) — goals and shots, AI-suggested (experimental, low-
            confidence) or coach-logged. Independent of analysis status —
            a coach can log events by hand even without a completed CV
            analysis (or without any video at all). */}
        <div id="events-section" className="bg-[#121212] border border-zinc-800 p-6 rounded-sm lg:col-span-2" data-testid="events-section">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <h2 className="font-heading text-2xl text-white flex items-center gap-2"><SoccerBall size={22} weight="fill" className="text-[#CCFF00]" /> Events</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {(game.video_url || game.video_upload_id || game.bunny_video_id) && (
                <div className="flex items-center gap-2 flex-wrap bg-[#0A0A0A] border border-zinc-800 rounded-sm px-2.5 py-1.5" data-testid="highlights-reel-controls">
                  <span className="font-label text-[9px] text-zinc-500 uppercase">Reel:</span>
                  {["goal", "save", "shot", "yellow_card", "red_card", "highlight"].map((t) => (
                    <label key={t} className="flex items-center gap-1 text-xs text-zinc-300 cursor-pointer">
                      <input type="checkbox" checked={reelTypes.has(t)} onChange={() => toggleReelType(t)} data-testid={`reel-type-${t}`} className="w-3.5 h-3.5 accent-[#CCFF00]" />
                      {EVENT_TYPE_LABELS[t]}s
                    </label>
                  ))}
                  {highlightsActive ? (
                    <button type="button" onClick={stopHighlights} data-testid="stop-highlights-btn" className="flex items-center gap-1 font-label text-xs text-white hover:text-[#FF3B30] transition-colors duration-150">
                      <XCircle size={14} weight="bold" /> Stop
                    </button>
                  ) : (
                    <button type="button" onClick={startHighlights} disabled={reelEventsSorted().length === 0} data-testid="play-highlights-btn" className="flex items-center gap-1 font-label text-xs text-[#CCFF00] hover:text-[#B3E600] transition-colors duration-150 disabled:opacity-40 disabled:pointer-events-none">
                      <PlayCircle size={14} weight="fill" /> Play ({reelEventsSorted().length})
                    </button>
                  )}
                  {!isViewer && (
                    <button type="button" onClick={handleDownloadReel} disabled={reelEventsSorted().length === 0 || downloadingReel} data-testid="download-highlights-reel-btn" className="flex items-center gap-1 font-label text-xs text-zinc-400 hover:text-white transition-colors duration-150 disabled:opacity-40">
                      {downloadingReel ? <SpinnerGap size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />} {downloadingReel ? reelDownloadStage : "Download reel"}
                    </button>
                  )}
                </div>
              )}
              {!isViewer && !eventForm && (
                isScheduled ? (
                  <span
                    data-testid="add-event-disabled-hint"
                    title="Events can be added once this game has taken place"
                    className="font-label text-xs text-zinc-500 px-3 py-2"
                  >
                    Events can be added once this game has taken place
                  </span>
                ) : (
                  <button type="button" onClick={() => startAddEvent()} data-testid="add-event-btn" className="flex items-center gap-2 font-label text-xs bg-[#CCFF00] text-black px-3 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150">
                    <Plus size={14} weight="bold" /> Add event
                  </button>
                )
              )}
            </div>
          </div>
          <p className="text-xs text-zinc-500 max-w-xl mb-5">Confirmed goals update the score above automatically. AI-suggested goals are experimental and low-confidence — review before confirming. Shots are always logged by hand.</p>

          {eventForm && (
            <EventForm
              eventForm={eventForm} setEventForm={setEventForm} players={players} events={events}
              game={game} inputCls={inputCls} COACH_RECIPIENT_EVENT_TYPES={COACH_RECIPIENT_EVENT_TYPES}
              formatEventTime={formatEventTime} getCurrentPlaybackTime={getCurrentPlaybackTime}
              onCancel={cancelEventForm} onSave={saveEventForm}
            />
          )}

          <EventsList
            events={events} eventsLoading={eventsLoading} game={game} isViewer={isViewer}
            EVENT_TYPE_LABELS={EVENT_TYPE_LABELS} EVENT_TYPE_BADGE_CLS={EVENT_TYPE_BADGE_CLS}
            formatEventTime={formatEventTime} eventWhoLabel={eventWhoLabel} playerLabel={playerLabel}
            downloadingClipId={downloadingClipId} clipDownloadStage={clipDownloadStage} savingEventId={savingEventId}
            onJumpToEvent={jumpToEventMoment} onDownloadClip={handleDownloadClip} onConfirmEvent={confirmEvent}
            onEditEvent={startEditEvent} onDeleteEvent={setEventToDelete}
          />
        </div>

        <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm lg:col-span-2">
          <h2 className="font-heading text-2xl text-white mb-4">Coaches Comments</h2>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="game-notes" rows={4} placeholder="Match report, key moments, lineup notes…" disabled={!canManageGame} className={inputCls + " resize-none disabled:opacity-60"} />
        </div>

        {/* Advanced / AI Video Analysis — tracking, jersey OCR, player
            identification. Deliberately collapsed by default and relocated
            to the very bottom of the page (below Events and Coaches
            Comments) so it's clearly out of the primary Add-Event
            workflow — none of this code was removed or changed, only its
            position on the page. */}
        {(game.video_url || game.video_upload_id || game.bunny_video_id || (analysis && analysis.status !== "none")) && (
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="advanced-section-toggle"
              className="flex items-center gap-3 w-full bg-[#121212] border border-zinc-800 px-6 py-4 rounded-sm hover:border-zinc-700 transition-colors duration-150"
            >
              {showAdvanced ? <CaretDown size={16} className="text-zinc-500 shrink-0" /> : <CaretRight size={16} className="text-zinc-500 shrink-0" />}
              <span className="font-heading text-lg text-white">Advanced / AI Video Analysis</span>
              <span className="text-xs text-zinc-500 ml-auto hidden sm:inline">Tracking, jersey OCR &amp; player identification — optional</span>
            </button>
          </div>
        )}

        {/* Video analysis (V3 anonymous tracking) */}
        {showAdvanced && (game.video_url || game.video_upload_id || game.bunny_video_id) && (
          <VideoAnalysisCard
            analysis={analysis} canManageGame={canManageGame} analyzing={analyzing} cancelling={cancelling} now={now}
            stageLabel={stageLabel} elapsedLabel={elapsedLabel} formatMinutes={formatMinutes}
            onRunAnalysis={runAnalysis} onCancelAnalysis={cancelAnalysis} onOpenSettings={() => navigate("/settings")}
          />
        )}

        {/* Player identification (V4) — confirm/correct which roster player each track is */}
        {showAdvanced && analysis && analysis.status === "complete" && (
          <PlayerIdentificationSection
            tracks={tracks} tracksLoading={tracksLoading} categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            selectedTrackIds={selectedTrackIds} setSelectedTrackIds={setSelectedTrackIds} bulkActing={bulkActing}
            canManageGame={canManageGame} thumbIndexByTrack={thumbIndexByTrack} setThumbIndexByTrack={setThumbIndexByTrack}
            currentTeamId={currentTeamId} gameId={id} players={players} savingTrackId={savingTrackId}
            onJumpToMoment={jumpToVideoMoment} onUpdateTrackMatch={updateTrackMatch} onDismissTrack={dismissTrack}
            onRestoreTrack={restoreTrack} onBulkDismiss={bulkDismiss}
          />
        )}

        {error && <p className="text-[#FF3B30] text-sm lg:col-span-2" data-testid="detail-error">{error}</p>}

        {canManageGame && (
          <div className="lg:col-span-2 flex items-center justify-between">
            <button type="button" onClick={() => setShowDelete(true)} data-testid="delete-game-btn" className="flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs">
              <Trash size={16} weight="bold" /> Delete game
            </button>
            <button type="submit" disabled={saving} data-testid="save-game-detail-btn" className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-6 py-3 rounded-sm hover:bg-[#B3E600] hover:-translate-y-0.5 transition-all duration-150 disabled:opacity-50">
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </form>

      <AlertDialog open={!!eventToDelete} onOpenChange={(open) => !open && setEventToDelete(null)}>
        <AlertDialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="delete-event-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete event?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Are you sure you want to delete this event?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-event" className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { const ev = eventToDelete; setEventToDelete(null); await deleteEvent(ev); }}
              data-testid="confirm-delete-event"
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="bg-[#121212] border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete game?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">This permanently removes the game vs {game.opponent}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete-game" className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
